import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  capabilitiesFor,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { CodeBlock } from '@/components/Ui/CodeBlock';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { UiLink } from '@/components/Ui/UiLink';
import { CHALLENGE_ALGORITHM_ARGON2ID, CHALLENGE_ALGORITHM_PBKDF2 } from '@/lib/altcha';
import {
  captchaScript,
  captchaWidget,
  challengeUrl,
  curlExample,
  embedSnippet,
} from '@/lib/form-site';
import { apiReferenceUrl } from '@/lib/origins';
import classes from './FormOverview.module.css';
import type { SectionProps } from './types';

const ALGORITHM_LABELS: Record<string, string> = {
  [CHALLENGE_ALGORITHM_PBKDF2]: 'PBKDF2',
  [CHALLENGE_ALGORITHM_ARGON2ID]: 'Argon2id',
};

export function DeveloperSection({
  form,
  save,
  spec,
}: Pick<SectionProps, 'form' | 'save' | 'spec'>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const caps = useIntegrationCapabilities();
  const webhook = capabilitiesFor(caps, 'generic_webhook');
  const algorithm =
    form.challenge_algorithm === CHALLENGE_ALGORITHM_ARGON2ID
      ? CHALLENGE_ALGORITHM_ARGON2ID
      : CHALLENGE_ALGORITHM_PBKDF2;

  return (
    <Section
      id="developer"
      title={t('form.dev.title')}
      subtitle={t('form.dev.subtitle')}
      actions={
        <Button size="xs" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t('ui.hide') : t('ui.show')}
        </Button>
      }
    >
      {open && (
        <>
          <LedgerRow
            label={t('form.dev.algorithm')}
            value={ALGORITHM_LABELS[algorithm]}
            help={t('form.dev.algorithmHelp')}
            changed={
              algorithm === CHALLENGE_ALGORITHM_PBKDF2
                ? undefined
                : {
                    defaultLabel: 'PBKDF2',
                    onReset: () =>
                      void save(
                        { challenge_algorithm: CHALLENGE_ALGORITHM_PBKDF2 },
                        t('form.dev.algorithmSaved', { name: 'PBKDF2' })
                      ),
                  }
            }
            editor={{
              render: (close) => (
                <SegmentedButtons
                  aria-label={t('form.dev.algorithm')}
                  options={[
                    { value: CHALLENGE_ALGORITHM_PBKDF2, label: 'PBKDF2' },
                    { value: CHALLENGE_ALGORITHM_ARGON2ID, label: 'Argon2id' },
                  ]}
                  value={algorithm}
                  onChange={(v) => {
                    close();
                    void save(
                      { challenge_algorithm: v },
                      t('form.dev.algorithmSaved', { name: ALGORITHM_LABELS[v] })
                    );
                  }}
                />
              ),
            }}
          />
          <LedgerRow
            label={t('form.dev.rejections')}
            value={
              <p className={classes.devList}>
                {(['400', '402', '403', '429'] as const).map((code) => (
                  <span key={code} className={classes.line}>
                    {code} {t(`form.dev.status.${code}`)}
                  </span>
                ))}
              </p>
            }
          />
          <LedgerRow
            label={t('form.dev.options')}
            value={
              <p className={classes.devList}>
                <span className={classes.line}>?redirect=html | none</span>
                <span className={classes.line}>
                  ?css_framework= · ?css_embed= · ?iframe= · ?track=
                </span>
              </p>
            }
            help={t('form.dev.optionsHelp')}
          />
          <div className={classes.block}>
            <strong>{t('form.dev.captchaUrl')}</strong>
            <CodeBlock code={challengeUrl(form.id)} copy />
            <strong>{t('form.dev.captchaScript')}</strong>
            <CodeBlock code={captchaScript()} copy />
            <CodeBlock code={captchaWidget(form.id)} copy />
            <strong>curl</strong>
            <CodeBlock code={curlExample(form.id, spec, form.allowed_origins?.[0])} copy />
            {form.check_challenge && (
              <span className={classes.help}>{t('form.dev.curlCaptcha')}</span>
            )}
            <strong>{t('form.dev.embed')}</strong>
            <CodeBlock code={embedSnippet(form.id, 'bulma', t)} copy wrap />
            <span className={classes.help}>{t('form.dev.embedHelp')}</span>
            <strong>{t('form.dev.signatures')}</strong>
            <span className={classes.help}>
              {webhook.signature_header
                ? t('form.dev.signaturesHelp', {
                    signature: webhook.signature_header,
                    idempotency: webhook.idempotency_header ?? '',
                  })
                : t('form.dev.signaturesUnknown')}{' '}
              <UiLink to="/account/developers">{t('form.dev.developersPage')}</UiLink>
            </span>
            <div>
              <Button
                component="a"
                size="xs"
                href={apiReferenceUrl()}
                target="_blank"
                rel="noreferrer"
              >
                {t('form.dev.apiReference')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Section>
  );
}
